import { FC, useRef, useState } from "react";
import orderOptimizerTranslationsHelper from "../../../../../languages/order-optimizer-translations.helper";
import ButtonComponent from "../../../../../common/components/button/button.component";
import useKeyboardPress from "../../../../../common/hooks/use-keyboard-press";
import OrderOptimizerRailyListingFilter from "../common/types/order-optimizer-raily-listing-filter";
import OrderOptimizerRailyListingFilterType from "../common/types/order-optimizer-raily-listing-filter-type";
import ListingFilterComponent from "../../../../../common/components/listing/filter/listing-filter.component";
import ListingFilterDefinition from "../../../../../common/components/listing/filter/types/listing-filter-definition";
import orderOptimizerRailyListingFiltersHelper from "./order-optimizer-raily-listing-filters.helper";
import OrderOptimizerRailyListingMaximumDelayModalComponent from "../maximum-delay-modal/order-optimizer-raily-listing-maximum-delay-modal.component";
import OrderOptimizerRailyListingMaximumHaltingTimeModalComponent from "../maximum-halting-time-modal/order-optimizer-raily-listing-maximum-halting-time-modal.component";
import useOpen from "../../../../../common/hooks/use-open";

type OrderOptimizerRailyListingFiltersProps = {
  filters: OrderOptimizerRailyListingFilter[];
  isListingLoading: boolean;
  onFiltersChange: (filters: OrderOptimizerRailyListingFilter[]) => void;
};

const OrderOptimizerRailyListingFiltersComponent: FC<
  OrderOptimizerRailyListingFiltersProps
> = (props) => {
  const translations =
    orderOptimizerTranslationsHelper.getRailyListingTranslations().filters;

  const maximumDelayModal = useOpen();
  const maximumHaltingTimeModal = useOpen();

  const [maximumDelayValue, setMaximumDelayValue] = useState<number>(0);
  const [maximumHaltingTimeValue, setMaximumHaltingTimeValue] =
    useState<number>(0);

  const confirmMaximumDelay = () => {
    const newFilter: OrderOptimizerRailyListingFilter = {
      type: OrderOptimizerRailyListingFilterType.MAXIMUM_DELAY,
      value: maximumDelayValue,
    };
    props.onFiltersChange([
      ...props.filters.filter(
        (filter) =>
          filter.type !== OrderOptimizerRailyListingFilterType.MAXIMUM_DELAY,
      ),
      newFilter,
    ]);

    maximumDelayModal.close();
  };

  const confirmMaximumHaltingTime = () => {
    const newFilter: OrderOptimizerRailyListingFilter = {
      type: OrderOptimizerRailyListingFilterType.MAXIMUM_HALTING_TIME,
      value: maximumHaltingTimeValue,
    };
    props.onFiltersChange([
      ...props.filters.filter(
        (filter) =>
          filter.type !==
          OrderOptimizerRailyListingFilterType.MAXIMUM_HALTING_TIME,
      ),
      newFilter,
    ]);

    maximumHaltingTimeModal.close();
  };

  const openMaximumDelayModal = () => {
    setMaximumDelayValue(0);
    maximumDelayModal.open();
  };

  const closeMaximumDelayModal = () => {
    maximumDelayModal.close();
    setMaximumDelayValue(0);
  };

  const openMaximumHaltingTimeModal = () => {
    setMaximumHaltingTimeValue(0);
    maximumHaltingTimeModal.open();
  };

  const closeMaximumHaltingTimeModal = () => {
    maximumHaltingTimeModal.close();
    setMaximumHaltingTimeValue(0);
  };

  const [filterSearchQuery, setFilterSearchQuery] = useState<{
    orderHumanId: number | null;
    maximumDelay: number | null;
    maximumHaltingTime: number | null;
    ignoreDriverRoutes: boolean;
  }>(() => ({
    orderHumanId:
      props.filters.find(
        (filter) =>
          filter.type === OrderOptimizerRailyListingFilterType.ORDER_HUMAN_ID,
      )?.value ?? null,
    maximumDelay:
      props.filters.find(
        (filter) =>
          filter.type === OrderOptimizerRailyListingFilterType.MAXIMUM_DELAY,
      )?.value ?? null,
    maximumHaltingTime:
      props.filters.find(
        (filter) =>
          filter.type ===
          OrderOptimizerRailyListingFilterType.MAXIMUM_HALTING_TIME,
      )?.value ?? null,
    ignoreDriverRoutes: props.filters.some(
      (filter) =>
        filter.type ===
        OrderOptimizerRailyListingFilterType.IGNORE_DRIVER_ROUTES,
    ),
  }));

  const inputRef = useRef<HTMLInputElement>(null);

  useKeyboardPress({
    Enter: () => addFilter(),
    ref: inputRef,
  });

  const addFilter = () => {
    const newAppliedFilters: OrderOptimizerRailyListingFilter[] = [];

    if (filterSearchQuery.orderHumanId) {
      newAppliedFilters.push({
        type: OrderOptimizerRailyListingFilterType.ORDER_HUMAN_ID,
        value: filterSearchQuery.orderHumanId,
      });
    }

    if (filterSearchQuery.maximumDelay) {
      newAppliedFilters.push({
        type: OrderOptimizerRailyListingFilterType.MAXIMUM_DELAY,
        value: filterSearchQuery.maximumDelay,
      });
    }

    if (filterSearchQuery.maximumHaltingTime) {
      newAppliedFilters.push({
        type: OrderOptimizerRailyListingFilterType.MAXIMUM_HALTING_TIME,
        value: filterSearchQuery.maximumHaltingTime,
      });
    }

    if (filterSearchQuery.ignoreDriverRoutes) {
      newAppliedFilters.push({
        type: OrderOptimizerRailyListingFilterType.IGNORE_DRIVER_ROUTES,
        value: filterSearchQuery.ignoreDriverRoutes,
      });
    }

    props.onFiltersChange(newAppliedFilters);
  };

  const filterDefinition: ListingFilterDefinition<OrderOptimizerRailyListingFilter> =
    orderOptimizerRailyListingFiltersHelper().getFilterDefinition(
      openMaximumDelayModal,
      openMaximumHaltingTimeModal,
    );

  return (
    <div className="order_optimizer_raily_listing_filter">
      <div className="order_optimizer_raily_listing_filter__input_wrapper">
        <ListingFilterComponent
          appliedFilters={props.filters}
          filterDefinition={filterDefinition}
          onFiltersChange={(appliedFilters) => {
            props.onFiltersChange(
              appliedFilters as OrderOptimizerRailyListingFilter[],
            );
          }}
          sortDefinition={{ options: [] }}
          appliedSortKey=""
          onSortKeyChange={() => {}}
        />
      </div>
      <ButtonComponent
        onClick={() => {
          setFilterSearchQuery({
            orderHumanId: null,
            maximumDelay: null,
            maximumHaltingTime: null,
            ignoreDriverRoutes: false,
          });
          props.onFiltersChange([]);
        }}
        isLoading={props.isListingLoading}
        classNames={{
          root: "order_optimizer_raily_listing_filter__button",
        }}
        title={translations.getAllOrdersButtonLabel}
        type="primary"
        idForTesting="order-optimizer-listing-filter-button"
      >
        {translations.getAllOrdersButtonLabel}
      </ButtonComponent>
      <OrderOptimizerRailyListingMaximumDelayModalComponent
        isOpen={maximumDelayModal.isOpen}
        maximumDelayValue={maximumDelayValue}
        onCloseClick={closeMaximumDelayModal}
        onSuccessClick={confirmMaximumDelay}
        setMaximumDelayValue={setMaximumDelayValue}
      />
      <OrderOptimizerRailyListingMaximumHaltingTimeModalComponent
        isOpen={maximumHaltingTimeModal.isOpen}
        maximumHaltingTimeValue={maximumHaltingTimeValue}
        onCloseClick={closeMaximumHaltingTimeModal}
        onSuccessClick={confirmMaximumHaltingTime}
        setMaximumHaltingTimeValue={setMaximumHaltingTimeValue}
      />
    </div>
  );
};

export default OrderOptimizerRailyListingFiltersComponent;
